import { useEffect, useState, type PropsWithChildren } from 'react';
import { refreshSession } from '../api/auth';
import { useAuth } from '../hooks/useAuth';
import {
  clearAuthSessionActive,
  hasAuthSessionActive,
  isAuthSessionValid,
} from '../util/authSession';
import { LoginPage } from './LoginPage';

export const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

export const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const { token, setToken } = useAuth();
  const [isBootstrapping, setIsBootstrapping] = useState(
    () => !isAuthSessionValid(token?.expiry) && hasAuthSessionActive(),
  );

  useEffect(() => {
    if (isAuthSessionValid(token?.expiry)) {
      setIsBootstrapping(false);

      const refresh = () => {
        refreshSession()
          .then((session) => setToken(session))
          .catch(() => undefined);
      };

      const intervalId = window.setInterval(refresh, TOKEN_REFRESH_INTERVAL_MS);

      return () => window.clearInterval(intervalId);
    }

    // No hint → show login. Do not auto-open Google popups (browsers block them
    // without a user gesture, and prompt=none still falls back to account UI).
    if (!hasAuthSessionActive()) {
      setIsBootstrapping(false);
      return;
    }

    let isCancelled = false;

    refreshSession()
      .then((session) => {
        if (!isCancelled) {
          setToken(session);
        }
      })
      .catch(() => {
        clearAuthSessionActive();
      })
      .finally(() => {
        if (!isCancelled) {
          setIsBootstrapping(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [setToken, token?.expiry]);

  if (isBootstrapping) return null;

  if (!isAuthSessionValid(token?.expiry)) return <LoginPage />;

  return children;
};
